import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getFlagImage } from "@/lib/general";
import useCurrencyStore from "@/stores/ui/currencyStore";
import { Loader2 } from "lucide-react";
import { Currency } from "@/types";
import CustomDropdown from "@/components/ui/CustomDropdown";

const currencies: Currency[] = [
  { code: "USD", country: "United States", name: "US Dollar", symbol: "$" },
  { code: "EUR", country: "European Union", name: "Euro", symbol: "€" },
  {
    code: "GBP",
    country: "United Kingdom",
    name: "British Pound",
    symbol: "£",
  },
  { code: "JPY", country: "Japan", name: "Japanese Yen", symbol: "¥" },
  {
    code: "AUD",
    country: "Australia",
    name: "Australian Dollar",
    symbol: "A$",
  },
  { code: "CAD", country: "Canada", name: "Canadian Dollar", symbol: "C$" },
];

export default function CurrencySelection() {
  const { selectedCurrency, setCurrency } = useCurrencyStore();
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const handleCurrencyChange = async (currency: Currency) => {
    setIsLoading(true);
    setCurrency(currency);
    setIsOpen(false);
    setIsLoading(false);
  };

  return (
    <CustomDropdown
      title="Currency"
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      trigger={
        <button className="relative h-10 w-10 2xl:h-11 2xl:w-11 flex items-center justify-center bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full hover:bg-gray-50 transition-colors duration-200 shadow-sm focus:outline-none">
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <img
              src={getFlagImage(selectedCurrency.country)}
              alt={selectedCurrency.country}
              className="w-6 h-6 2xl:w-[28px] 2xl:h-[28px]"
            />
          )}
        </button>
      }
      className="w-[16rem] bg-white rounded-xl mt-4 px-1"
    >
      {currencies.map((currency) => (
        <div
          key={currency.code}
          onClick={() => handleCurrencyChange(currency)}
          className={`${
            selectedCurrency.code === currency.code
              ? "bg-indigo-100/50"
              : "hover:bg-neutral-100"
          } flex items-center justify-between p-3 my-1 cursor-pointer rounded-lg`}
        >
          <div className="flex items-center gap-2">
            <img
              src={getFlagImage(currency.country)}
              alt={currency.country}
              className="w-5 h-5"
            />
            <span className="text-sm font-light">{currency.name}</span>
          </div>
          <span className="text-xs text-neutral-500 font-light">
            {currency.code}
          </span>
        </div>
      ))}
    </CustomDropdown>
  );
}
